/**
 * Estrategia de Validación Adaptativa por Formato
 * 
 * GEO-01: Ajusta la intensidad de validación según el formato del documento
 * y la calidad de las coordenadas. Optimiza consultas reduciendo validación
 * para datos de alta calidad y aumentando para formatos problemáticos.
 * 
 * PRINCIPIO: Gastar recursos donde más se necesitan
 * - Texto plano + score alto → casi no necesita validación
 * - ODT + score bajo → validación agresiva multi-fuente
 * 
 * IMPACTO ESPERADO:
 * - +12 puntos score promedio
 * - -30% consultas WFS innecesarias
 * - Mayor precisión en formatos problemáticos
 * 
 * @module services/geocoding/validationStrategy
 */

import type { DocumentFormat } from '@/utils/formatDetector';
import { validateCoordinatesInMunicipality } from '@/lib/spatialValidation';

// ============================================================================
// TIPOS
// ============================================================================

/** Nivel de validación cruzada */
export type ValidationLevel = 'light' | 'standard' | 'strict';

/** Configuración de validación según nivel */
export interface ValidationConfig {
  /** Nivel de validación */
  level: ValidationLevel;
  
  /** Mínimo de fuentes a consultar */
  minSources: number;
  
  /** Umbral de discrepancia aceptable (metros) */
  discrepancyThreshold: number;
  
  /** Timeout por fuente (ms) */
  sourceTimeout: number;
  
  /** Requiere consenso geográfico */
  requireConsensus: boolean;
  
  /** Score mínimo para aceptar sin revisión */
  minScoreForAutoAccept: number;
  
  /** Validar coordenadas contra límite municipal (TRF-05) */
  validateSpatialBoundary: boolean;
}

/** Contexto para determinar estrategia */
export interface ValidationContext {
  /** Formato del documento de origen */
  format: DocumentFormat;
  
  /** Score de las coordenadas (0-100) */
  coordScore: number;
  
  /** Tipología de la infraestructura */
  tipologia?: string;
  
  /** Es infraestructura crítica (hospital, bomberos) */
  isCritical?: boolean;
  
  /** Tiene dirección como alternativa */
  hasAddress?: boolean;
}

/** Resultado de determinación de estrategia */
export interface StrategyResult {
  /** Configuración de validación */
  config: ValidationConfig;
  
  /** Razón de la decisión */
  reason: string;
  
  /** Factores que influyeron */
  factors: string[];
}

// ============================================================================
// CONFIGURACIONES POR NIVEL
// ============================================================================

/**
 * Configuraciones de validación por nivel
 */
export const VALIDATION_CONFIGS: Record<ValidationLevel, ValidationConfig> = {
  /**
   * LIGHT: Para datos de alta calidad
   * - Texto plano con coordenadas limpias
   * - Spreadsheet bien formateado
   * - Score >= 90
   */
  light: {
    level: 'light',
    minSources: 1,
    discrepancyThreshold: 200, // Tolerancia alta
    sourceTimeout: 3000,
    requireConsensus: false,
    minScoreForAutoAccept: 85,
    validateSpatialBoundary: false, // Omitir en validación ligera
  },
  
  /**
   * STANDARD: Caso común
   * - La mayoría de documentos
   * - Score entre 70-89
   */
  standard: {
    level: 'standard',
    minSources: 2,
    discrepancyThreshold: 100,
    sourceTimeout: 5000,
    requireConsensus: true,
    minScoreForAutoAccept: 75,
    validateSpatialBoundary: true, // Validar en modo estándar
  },
  
  /**
   * STRICT: Para datos problemáticos
   * - ODT con corrupción UTF-8
   * - Score < 70
   * - Infraestructura crítica
   */
  strict: {
    level: 'strict',
    minSources: 3,
    discrepancyThreshold: 50, // Tolerancia baja
    sourceTimeout: 8000,
    requireConsensus: true,
    minScoreForAutoAccept: 90,
    validateSpatialBoundary: true, // Siempre validar en estricto
  },
};

// ============================================================================
// UMBRALES POR FORMATO
// ============================================================================

/**
 * Score esperado por formato (basado en análisis real)
 * Permite ajustar expectativas según origen
 */
export const FORMAT_EXPECTED_SCORES: Record<DocumentFormat, { min: number; expected: number; max: number }> = {
  plain: { min: 85, expected: 95, max: 100 },
  spreadsheet: { min: 80, expected: 92, max: 100 },
  markdown: { min: 70, expected: 85, max: 95 },
  html: { min: 65, expected: 80, max: 95 },
  odt: { min: 50, expected: 70, max: 90 },
};

/**
 * Tipologías de infraestructura crítica
 * Siempre requieren validación STRICT
 */
// T2 — Plan Umbral Dual 90/85 (2026-03-25)
// Añadidas: ABASTECIMIENTO, DEPOSITO, EDAR, SUBESTACION, EDUCATIVO
// EDUCATIVO: Decisión Opción A — centros educativos como infraestructuras
// de acogida según Decreto 69/2024.
//
// TD-EMERGENCIA (deuda técnica detectada 2026-03-25):
// El sistema mantiene dos representaciones del mismo concepto:
//   - InfrastructureType.EMERGENCY = 'EMERGENCIAS' (enum canónico, types/infrastructure.ts)
//   - mapTypeToTipologia(EMERGENCY)  = 'EMERGENCIA'  (hardcodeado, orchestrator/typeMapping.ts)
// El Orchestrator pasa por typeMapping → obtiene 'EMERGENCIA' → llama a isCriticalInfrastructure.
// Ambas formas deben estar aquí hasta que se unifique en una tarea dedicada (TD-EMERGENCIA).
// NO eliminar ninguna de las dos sin resolver TD-EMERGENCIA primero.
export const CRITICAL_INFRASTRUCTURE: string[] = [
  'SANITARIO',
  'SEGURIDAD',
  'BOMBEROS',
  'EMERGENCIAS',  // enum canónico: InfrastructureType.EMERGENCY = 'EMERGENCIAS'
  'EMERGENCIA',   // TD-EMERGENCIA: mapTypeToTipologia(EMERGENCY) → 'EMERGENCIA' (sin S)
  'HOSPITAL',
  'URGENCIAS',
  'UCI',
  'QUIROFANO',
  'ABASTECIMIENTO',
  'DEPOSITO',
  'EDAR',
  'SUBESTACION',
  'EDUCATIVO',
];

// ============================================================================
// FUNCIONES PRINCIPALES
// ============================================================================

/**
 * Determina el nivel de validación apropiado según contexto.
 * 
 * Algoritmo de decisión:
 * 1. Infraestructura crítica → STRICT (siempre)
 * 2. Texto plano + score >= 90 → LIGHT
 * 3. Spreadsheet + score >= 85 → LIGHT
 * 4. ODT + score < 80 → STRICT
 * 5. HTML + score < 70 → STRICT
 * 6. Default → STANDARD
 */
export function determineValidationLevel(context: ValidationContext): ValidationLevel {
  const { format, coordScore, tipologia, isCritical } = context;
  
  // 1. Infraestructura crítica siempre requiere STRICT
  if (isCritical) {
    return 'strict';
  }
  
  // Verificar si tipología es crítica
  if (tipologia) {
    const upperTipo = tipologia.toUpperCase();
    if (CRITICAL_INFRASTRUCTURE.some(c => upperTipo.includes(c))) {
      return 'strict';
    }
  }
  
  // 2. Por formato y score
  switch (format) {
    case 'plain':
      // Texto plano con alto score = muy confiable
      if (coordScore >= 90) return 'light';
      if (coordScore >= 75) return 'standard';
      return 'strict';
      
    case 'spreadsheet':
      // Spreadsheet suele ser limpio
      if (coordScore >= 85) return 'light';
      if (coordScore >= 70) return 'standard';
      return 'strict';
      
    case 'markdown':
      // Markdown puede tener artefactos
      if (coordScore >= 85) return 'standard';
      return 'strict';
      
    case 'html':
      // HTML puede tener entidades
      if (coordScore >= 80) return 'standard';
      return 'strict';
      
    case 'odt':
      // ODT es el más problemático
      if (coordScore >= 90) return 'standard';
      if (coordScore >= 70) return 'standard';
      return 'strict';
      
    default:
      return 'standard';
  }
}

/**
 * Obtiene la configuración completa de validación para un contexto.
 */
export function getValidationStrategy(context: ValidationContext): StrategyResult {
  const level = determineValidationLevel(context);
  const config = VALIDATION_CONFIGS[level];
  
  // Recopilar factores de decisión
  const factors: string[] = [];
  
  if (context.isCritical) {
    factors.push('infraestructura crítica');
  }
  
  if (context.tipologia) {
    const upperTipo = context.tipologia.toUpperCase();
    if (CRITICAL_INFRASTRUCTURE.some(c => upperTipo.includes(c))) {
      factors.push(`tipología crítica: ${context.tipologia}`);
    }
  }
  
  factors.push(`formato: ${context.format}`);
  factors.push(`score coordenadas: ${context.coordScore}`);
  
  const expected = FORMAT_EXPECTED_SCORES[context.format];
  if (context.coordScore < expected.min) {
    factors.push(`score bajo para ${context.format} (esperado ≥${expected.min})`);
  } else if (context.coordScore >= expected.max) {
    factors.push(`score óptimo para ${context.format}`);
  }
  
  // Generar razón
  let reason: string;
  switch (level) {
    case 'light':
      reason = 'Datos de alta calidad, validación mínima suficiente';
      break;
    case 'strict':
      reason = 'Datos problemáticos o críticos, validación exhaustiva requerida';
      break;
    default:
      reason = 'Validación estándar para casos normales';
  }
  
  return { config, reason, factors };
}

/**
 * Calcula ahorro estimado de consultas al usar validación adaptativa.
 */
export function estimateQuerySavings(
  records: Array<{ format: DocumentFormat; coordScore: number }>
): { totalRecords: number; savedQueries: number; savingsPercent: number } {
  const totalRecords = records.length;
  let savedQueries = 0;
  
  for (const record of records) {
    const level = determineValidationLevel({
      format: record.format,
      coordScore: record.coordScore,
    });
    
    // STANDARD usa 2 fuentes, LIGHT usa 1, STRICT usa 3
    // Ahorro = (STANDARD_SOURCES - ACTUAL_SOURCES)
    const standardSources = VALIDATION_CONFIGS.standard.minSources;
    const actualSources = VALIDATION_CONFIGS[level].minSources;
    
    if (actualSources < standardSources) {
      savedQueries += (standardSources - actualSources);
    }
  }
  
  const savingsPercent = totalRecords > 0 
    ? Math.round((savedQueries / (totalRecords * VALIDATION_CONFIGS.standard.minSources)) * 100)
    : 0;
  
  return { totalRecords, savedQueries, savingsPercent };
}

/**
 * Verifica si el score cumple expectativas para el formato.
 */
export function meetsFormatExpectations(format: DocumentFormat, score: number): boolean {
  const expected = FORMAT_EXPECTED_SCORES[format];
  return score >= expected.min;
}

/**
 * Obtiene la calificación de calidad relativa al formato.
 */
export function getQualityRating(
  format: DocumentFormat, 
  score: number
): 'excellent' | 'good' | 'acceptable' | 'poor' {
  const expected = FORMAT_EXPECTED_SCORES[format];
  
  if (score >= expected.max) return 'excellent';
  if (score >= expected.expected) return 'good';
  if (score >= expected.min) return 'acceptable';
  return 'poor';
}

// ============================================================================
// VALIDACIÓN ESPACIAL POST-GEOCODING (TRF-05)
// ============================================================================

/** Confianza mínima tras validación espacial fallida (TRF-05) */
export const MIN_SPATIAL_CONFIDENCE = 0.3;

/** Estructura unificada de validación espacial */
export interface SpatialValidation {
  /** ¿Punto válido dentro del municipio? */
  isValid?: boolean;
  /** Mensaje descriptivo de la validación */
  message?: string;
}

/** Resultado con campos de validación espacial */
export interface SpatialValidatedResult {
  /** Resultado de geocodificación original (WGS84) */
  coordenadas?: { lng: number; lat: number };
  confianza: number;
  /** Validación espacial (estructura unificada TRF-05/TRF-06) */
  spatialValidation?: SpatialValidation;
}

/**
 * Valida resultado de geocodificación contra límite municipal.
 * Solo ejecuta si validateSpatialBoundary=true y hay coordenadas.
 * Reduce confianza si punto fuera del municipio (mínimo MIN_SPATIAL_CONFIDENCE).
 * 
 * MEJORAS TRF-05 AUDITORÍA:
 * - Try-catch con graceful degradation
 * - Confianza mínima 0.3 (no 0) para puntos fuera
 * - Manejo robusto de errores inesperados
 * 
 * @param result - Resultado con coordenadas y confianza
 * @param municipio - Nombre o INE del municipio esperado
 * @param config - Configuración de validación
 * @returns Resultado con spatialValidation (estructura unificada TRF-05/TRF-06)
 */
/** Tipo de retorno con spatialValidation garantizado */
export type WithSpatialValidation<T> = T & { spatialValidation?: SpatialValidation };

export function applySpatialValidation<T extends { coordenadas?: { lng: number; lat: number }; confianza: number }>(
  result: T,
  municipio: string,
  config: ValidationConfig
): WithSpatialValidation<T> {
  // Omitir si no está habilitada o no hay coordenadas
  if (!config.validateSpatialBoundary || !result.coordenadas) {
    return result;
  }

  try {
    const coords: [number, number] = [result.coordenadas.lng, result.coordenadas.lat];
    const spatial = validateCoordinatesInMunicipality(coords, municipio);

    // Calcular nueva confianza: si fuera, reducir pero no a 0
    const newConfianza = spatial.isValid
      ? result.confianza
      : Math.max(MIN_SPATIAL_CONFIDENCE, Math.min(result.confianza, spatial.confidence));

    return {
      ...result,
      spatialValidation: {
        isValid: spatial.isValid,
        message: spatial.message,
      },
      confianza: newConfianza,
    };
  } catch (error) {
    // Graceful degradation: no fallar, solo advertir
    const errorMsg = error instanceof Error ? error.message : 'Error desconocido';
    return {
      ...result,
      spatialValidation: {
        isValid: undefined,
        message: `Error validación espacial: ${errorMsg}`,
      },
      // Mantener confianza original en caso de error
    };
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  determineValidationLevel,
  getValidationStrategy,
  estimateQuerySavings,
  meetsFormatExpectations,
  getQualityRating,
  applySpatialValidation,
  VALIDATION_CONFIGS,
  FORMAT_EXPECTED_SCORES,
  CRITICAL_INFRASTRUCTURE,
  MIN_SPATIAL_CONFIDENCE,
};
